import {
  Alert,
  Anchor,
  Badge,
  Button,
  Chip,
  Group,
  Progress,
  Select,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconPlus,
  IconRadar,
  IconSearch,
  IconSelector,
  IconX,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { MOCK_PRODUCT_LINES, RETAILERS } from "../../mock/products";
import { MOCK_BRANDS } from "../../mock/brands";
import { LogoImg } from "../../components/LogoImg";
import type { ProductLine } from "../../mock/products";
import { formatPriceCompact } from "../../utils/price";
import { getBrandStaleness } from "../../hooks/useBrandStaleness";

// --- Helpers ---

function lowestPrice(pl: ProductLine): number | null {
  const prices = pl.variants
    .map((v) => v.currentPrice ?? v.msrp)
    .filter((p): p is number => p !== null);
  return prices.length > 0 ? Math.min(...prices) : null;
}

function priceRange(pl: ProductLine): string {
  const prices = pl.variants
    .map((v) => v.currentPrice ?? v.msrp)
    .filter((p): p is number => p !== null)
    .sort((a, b) => a - b);
  if (prices.length === 0) return "—";
  if (prices.length === 1) return formatPriceCompact(prices[0]);
  return `${formatPriceCompact(prices[0])} – ${formatPriceCompact(prices[prices.length - 1])}`;
}

function enrichmentColor(status: string): string {
  if (status === "complete") return "green";
  if (status === "partial") return "yellow";
  return "gray";
}

function retailerCoverage(pl: ProductLine) {
  const perRetailer = RETAILERS.map((r) => {
    let matched = 0;
    let total = 0;
    for (const vrm of pl.retailerMap) {
      const rm = vrm.retailers.find((x) => x.retailerId === r.id);
      if (rm && rm.status !== "pending") {
        total++;
        if (rm.status === "matched") matched++;
      }
    }
    return { name: r.name, domain: r.domain, matched, total };
  });
  return {
    matched: perRetailer.reduce((s, r) => s + r.matched, 0),
    total: perRetailer.reduce((s, r) => s + r.total, 0),
    perRetailer,
  };
}

function modelNumberCoverage(pl: ProductLine): { has: number; total: number } {
  return {
    total: pl.variants.length,
    has: pl.variants.filter((v) => v.modelNumber).length,
  };
}

// --- Sort ---

type SortField = "name" | "category" | "sizes" | "price" | "models" | "enrichment";
type SortDir = "asc" | "desc";

function sortProducts(products: ProductLine[], field: SortField, dir: SortDir): ProductLine[] {
  const m = dir === "asc" ? 1 : -1;
  return [...products].sort((a, b) => {
    switch (field) {
      case "name":
        return m * a.name.localeCompare(b.name);
      case "category":
        return m * a.categoryName.localeCompare(b.categoryName);
      case "sizes":
        return m * (a.variants.length - b.variants.length);
      case "price": {
        const pa = lowestPrice(a) ?? Infinity;
        const pb = lowestPrice(b) ?? Infinity;
        return m * (pa - pb);
      }
      case "models": {
        const ma = modelNumberCoverage(a);
        const mb = modelNumberCoverage(b);
        return m * (ma.has / Math.max(ma.total, 1) - mb.has / Math.max(mb.total, 1));
      }
      case "enrichment": {
        const order = { complete: 0, partial: 1, pending: 2 };
        const ea = order[a.enrichmentStatus as keyof typeof order] ?? 3;
        const eb = order[b.enrichmentStatus as keyof typeof order] ?? 3;
        return m * (ea - eb);
      }
      default:
        return 0;
    }
  });
}

// --- Components ---

function SortableHeader({
  label,
  field,
  currentField,
  currentDir,
  onSort,
  ta,
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (field: SortField) => void;
  ta?: string;
}) {
  const active = currentField === field;
  return (
    <Table.Th style={{ textAlign: ta as "left" | "right" | "center" | undefined }}>
      <UnstyledButton onClick={() => onSort(field)}>
        <Group gap={4} wrap="nowrap" justify={ta === "right" ? "flex-end" : ta === "center" ? "center" : "flex-start"}>
          <Text size="xs" fw={600} c={active ? undefined : "dimmed"}>
            {label}
          </Text>
          {active ? (
            currentDir === "asc" ? (
              <IconArrowUp size={14} />
            ) : (
              <IconArrowDown size={14} />
            )
          ) : (
            <IconSelector size={14} style={{ opacity: 0.3 }} />
          )}
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

function RetailerDots({ pl }: { pl: ProductLine }) {
  const coverage = retailerCoverage(pl);
  return (
    <Group gap={3}>
      {coverage.perRetailer.map((r) => {
        const pct = r.total > 0 ? r.matched / r.total : 0;
        const color =
          r.total === 0
            ? "gray"
            : pct === 1
              ? "green"
              : pct > 0
                ? "yellow"
                : "red";
        return (
          <Tooltip
            key={r.name}
            label={`${r.name}: ${r.matched}/${r.total} sizes matched`}
          >
            <LogoImg
              domain={r.domain}
              alt={r.name}
              size={16}
              style={{
                opacity: r.total === 0 ? 0.2 : 1,
                outline:
                  color === "green"
                    ? "2px solid var(--mantine-color-green-5)"
                    : color === "yellow"
                      ? "2px solid var(--mantine-color-yellow-5)"
                      : color === "red"
                        ? "2px solid var(--mantine-color-red-5)"
                        : "1px solid var(--mantine-color-gray-3)",
                outlineOffset: 1,
              }}
            />
          </Tooltip>
        );
      })}
    </Group>
  );
}

// --- Page ---

export function ProductsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [staleBannerDismissed, setStaleBannerDismissed] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [filterEnrichment, setFilterEnrichment] = useState<string | null>(null);
  const [filterYear, setFilterYear] = useState<string | null>(null);

  // Derive filter options from data
  const categories = useMemo(
    () => [...new Set(MOCK_PRODUCT_LINES.map((pl) => pl.categoryName))].sort(),
    [],
  );
  const years = useMemo(
    () => [...new Set(MOCK_PRODUCT_LINES.map((pl) => String(pl.year)))].sort().reverse(),
    [],
  );

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  const staleBrands = useMemo(
    () =>
      MOCK_BRANDS.filter((b) => {
        const { level } = getBrandStaleness(b.lastOemRefreshAt);
        return level === "stale" || level === "critical";
      }),
    [],
  );

  const hasFilters = filterCategory || filterEnrichment || filterYear;

  function clearFilters() {
    setFilterCategory(null);
    setFilterEnrichment(null);
    setFilterYear(null);
  }

  // Filter pipeline
  const filtered = useMemo(() => {
    let result = MOCK_PRODUCT_LINES;

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(
        (pl) =>
          pl.name.toLowerCase().includes(q) ||
          pl.series.toLowerCase().includes(q) ||
          pl.brandName.toLowerCase().includes(q) ||
          pl.categoryName.toLowerCase().includes(q) ||
          pl.variants.some((v) => v.modelNumber?.toLowerCase().includes(q)),
      );
    }
    if (filterCategory) {
      result = result.filter((pl) => pl.categoryName === filterCategory);
    }
    if (filterEnrichment) {
      result = result.filter((pl) => pl.enrichmentStatus === filterEnrichment);
    }
    if (filterYear) {
      result = result.filter((pl) => String(pl.year) === filterYear);
    }

    return sortProducts(result, sortField, sortDir);
  }, [debouncedSearch, filterCategory, filterEnrichment, filterYear, sortField, sortDir]);

  // Summary stats (computed once from filtered)
  const stats = useMemo(() => {
    let totalSkus = 0;
    let totalMatched = 0;
    let totalPossible = 0;
    for (const pl of filtered) {
      totalSkus += pl.variants.length;
      const cov = retailerCoverage(pl);
      totalMatched += cov.matched;
      totalPossible += cov.total;
    }
    return { totalSkus, totalMatched, totalPossible };
  }, [filtered]);

  return (
    <>
      <Group justify="space-between" mb="lg">
        <Title order={2}>Product Lines</Title>
        <Group gap="sm">
          <Button
            variant="light"
            leftSection={<IconRadar size={16} />}
            onClick={() => navigate("/admin/discover")}
          >
            Discover Products
          </Button>
          <Button leftSection={<IconPlus size={16} />}>Add Manually</Button>
        </Group>
      </Group>

      {/* Staleness Banner */}
      {staleBrands.length > 0 && !staleBannerDismissed && (
        <Alert
          variant="light"
          color="yellow"
          icon={<IconAlertTriangle size={16} />}
          withCloseButton
          onClose={() => setStaleBannerDismissed(true)}
          mb="md"
        >
          {staleBrands.length === 1
            ? `${staleBrands[0].name} has`
            : `${staleBrands.length} brands have`}{" "}
          stale OEM catalog data.{" "}
          <Anchor size="sm" onClick={() => navigate("/admin/brands")}>
            Review on Brands page
          </Anchor>
        </Alert>
      )}

      {/* Search + Filters */}
      <TextInput
        placeholder="Search by name, series, brand, category, or model number..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        mb="sm"
      />

      <Group gap="sm" mb="md">
        <Select
          placeholder="Category"
          data={categories}
          value={filterCategory}
          onChange={setFilterCategory}
          clearable
          size="xs"
          w={180}
        />
        <Select
          placeholder="Enrichment"
          data={[
            { value: "complete", label: "Complete" },
            { value: "partial", label: "Partial" },
            { value: "pending", label: "Pending" },
          ]}
          value={filterEnrichment}
          onChange={setFilterEnrichment}
          clearable
          size="xs"
          w={150}
        />
        <Select
          placeholder="Year"
          data={years}
          value={filterYear}
          onChange={setFilterYear}
          clearable
          size="xs"
          w={100}
        />
        {hasFilters && (
          <Chip
            checked={false}
            size="xs"
            variant="light"
            onClick={clearFilters}
          >
            <Group gap={4}>
              <IconX size={12} />
              Clear filters
            </Group>
          </Chip>
        )}
      </Group>

      {/* Summary */}
      <Group gap="xl" mb="md">
        <Text size="sm" c="dimmed">
          {filtered.length} product lines · {stats.totalSkus} SKUs
        </Text>
        <Group gap={4}>
          <Text size="sm" c="dimmed">
            Retailer coverage:
          </Text>
          <Progress
            value={(stats.totalMatched / Math.max(stats.totalPossible, 1)) * 100}
            size="sm"
            w={100}
            color="green"
          />
          <Text size="sm" c="dimmed">
            {stats.totalMatched}/{stats.totalPossible}
          </Text>
        </Group>
      </Group>

      {/* Table */}
      <Table.ScrollContainer minWidth={900}>
        <Table highlightOnHover verticalSpacing="sm" fz="sm">
          <Table.Thead>
            <Table.Tr>
              <SortableHeader label="Product Line" field="name" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Category" field="category" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
              <SortableHeader label="Sizes" field="sizes" currentField={sortField} currentDir={sortDir} onSort={handleSort} ta="center" />
              <SortableHeader label="Price Range" field="price" currentField={sortField} currentDir={sortDir} onSort={handleSort} ta="right" />
              <SortableHeader label="Model #s" field="models" currentField={sortField} currentDir={sortDir} onSort={handleSort} ta="center" />
              <Table.Th>Retailers</Table.Th>
              <SortableHeader label="Enrichment" field="enrichment" currentField={sortField} currentDir={sortDir} onSort={handleSort} ta="center" />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7} ta="center" py="xl">
                  <Text c="dimmed">
                    {debouncedSearch || hasFilters
                      ? "No product lines match your search and filters."
                      : "No product lines yet. Use Discover to import products."}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filtered.map((pl) => {
                const models = modelNumberCoverage(pl);
                return (
                  <Table.Tr
                    key={pl.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/admin/products/${pl.id}`)}
                  >
                    <Table.Td>
                      <Group gap="sm">
                        <LogoImg domain={pl.brandDomain} alt={pl.brandName} size={20} />
                        <div>
                          <Text size="sm" fw={600}>
                            {pl.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {pl.year} · {pl.displayTechnology}
                          </Text>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {pl.categoryName}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Group gap={4} justify="center">
                        {pl.variants.map((v) => (
                          <Badge
                            key={v.id}
                            variant="light"
                            color="gray"
                            size="xs"
                          >
                            {v.variantLabel}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={500}>
                        {priceRange(pl)}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Text
                        size="xs"
                        c={
                          models.has === models.total
                            ? "green"
                            : models.has > 0
                              ? "yellow"
                              : "dimmed"
                        }
                        fw={500}
                      >
                        {models.has === models.total ? "✓ " : ""}
                        {models.has}/{models.total}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <RetailerDots pl={pl} />
                    </Table.Td>
                    <Table.Td ta="center">
                      <Badge
                        size="xs"
                        variant="light"
                        color={enrichmentColor(pl.enrichmentStatus)}
                      >
                        {pl.enrichmentStatus}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </>
  );
}
